import log from "logbeacon";
import { WebSocketClient } from "./WebSocketClient";
import { WebSocketConfig, WebSocketManagerConfig } from "./types";

/**
 * WebSocket 管理器
 * 管理多个 WebSocket 客户端实例
 * 支持单例模式(每个 URL 一个实例)或多实例模式
 */
export class WebSocketManager {
  private static instances: Map<string, WebSocketClient> = new Map();
  private static config: WebSocketManagerConfig = {
    singleton: true,
    defaultConfig: {},
  };

  /**
   * 配置管理器
   */
  static configure(config: WebSocketManagerConfig): void {
    this.config = {
      ...this.config,
      ...config,
    };
  }

  /**
   * 获取或创建 WebSocket 客户端实例
   * @param url WebSocket 服务器 URL
   * @param config WebSocket 配置
   * @param options 实例选项
   * @returns WebSocket 客户端实例
   */
  static getInstance(
    url: string,
    config: Partial<WebSocketConfig> = {},
    options: { singleton?: boolean } = {},
  ): WebSocketClient {
    const useSingleton = options.singleton ?? this.config.singleton ?? true;

    // 如果是单例模式,检查是否已存在实例
    if (useSingleton && this.instances.has(url)) {
      const instance = this.instances.get(url)!;

      // 如果提供了新配置,更新实例配置
      if (Object.keys(config).length > 0) {
        instance.updateConfig(config);
      }

      return instance;
    }

    // 创建新实例
    const fullConfig: WebSocketConfig = {
      url,
      ...this.config.defaultConfig,
      ...config,
    };

    const client = new WebSocketClient(fullConfig);

    // 如果是单例模式,保存实例
    if (useSingleton) {
      this.instances.set(url, client);
    }

    return client;
  }

  /**
   * 创建新的 WebSocket 客户端实例(不使用单例)
   * @param url WebSocket 服务器 URL
   * @param config WebSocket 配置
   * @returns 新的 WebSocket 客户端实例
   */
  static createInstance(
    url: string,
    config: Partial<WebSocketConfig> = {},
  ): WebSocketClient {
    return this.getInstance(url, config, { singleton: false });
  }

  /**
   * 检查是否存在指定 URL 的实例
   * @param url WebSocket 服务器 URL
   * @returns 是否存在实例
   */
  static hasInstance(url: string): boolean {
    return this.instances.has(url);
  }

  /**
   * 获取已存在的实例(不创建新实例)
   * @param url WebSocket 服务器 URL
   * @returns WebSocket 客户端实例或 undefined
   */
  static getExistingInstance(url: string): WebSocketClient | undefined {
    return this.instances.get(url);
  }

  /**
   * 移除指定 URL 的实例
   * @param url WebSocket 服务器 URL
   * @param destroy 是否销毁实例
   */
  static removeInstance(url: string, destroy: boolean = true): void {
    const instance = this.instances.get(url);

    if (instance) {
      if (destroy) {
        instance.destroy();
      }

      this.instances.delete(url);
    }
  }

  /**
   * 获取所有实例
   * @returns 所有实例的映射
   */
  static getAllInstances(): Map<string, WebSocketClient> {
    return new Map(this.instances);
  }

  /**
   * 获取所有实例的 URL 列表
   * @returns URL 列表
   */
  static getAllUrls(): string[] {
    return Array.from(this.instances.keys());
  }

  /**
   * 获取实例数量
   * @returns 实例数量
   */
  static getInstanceCount(): number {
    return this.instances.size;
  }

  /**
   * 连接所有实例
   */
  static connectAll(): void {
    this.instances.forEach((instance) => {
      instance.connect();
    });
  }

  /**
   * 断开所有实例
   */
  static disconnectAll(): void {
    this.instances.forEach((instance) => {
      instance.disconnect();
    });
  }

  /**
   * 销毁所有实例
   */
  static destroyAll(): void {
    const urls = Array.from(this.instances.keys());

    this.instances.forEach((instance, url) => {
      log.debug(`[WebSocketManager] 销毁实例: ${url}`);
      instance.destroy();
    });

    this.instances.clear();

    if (urls.length > 0) {
      log.debug(`[WebSocketManager] 已销毁 ${urls.length} 个实例`);
    }
  }

  /**
   * 清理已断开的实例
   */
  static cleanup(): void {
    const urlsToRemove: string[] = [];

    this.instances.forEach((instance, url) => {
      if (!instance.isConnected()) {
        urlsToRemove.push(url);
      }
    });

    urlsToRemove.forEach((url) => {
      this.removeInstance(url, true);
    });

    if (urlsToRemove.length > 0) {
      log.debug(
        `[WebSocketManager] 清理了 ${urlsToRemove.length} 个断开的实例`,
      );
    }
  }

  /**
   * 获取所有实例的状态信息
   */
  static getStatusInfo(): Array<{
    url: string;
    state: string;
    isConnected: boolean;
    subscriptions: number;
  }> {
    const status: Array<{
      url: string;
      state: string;
      isConnected: boolean;
      subscriptions: number;
    }> = [];

    this.instances.forEach((instance, url) => {
      const subscriptionInfo = instance.getSubscriptionInfo();
      status.push({
        url,
        state: instance.getState(),
        isConnected: instance.isConnected(),
        subscriptions: subscriptionInfo.length,
      });
    });

    return status;
  }

  /**
   * 按条件查找实例
   * @param predicate 查找条件
   * @returns 匹配的实例数组
   */
  static findInstances(
    predicate: (client: WebSocketClient, url: string) => boolean,
  ): Array<{ url: string; client: WebSocketClient }> {
    const result: Array<{ url: string; client: WebSocketClient }> = [];

    this.instances.forEach((client, url) => {
      if (predicate(client, url)) {
        result.push({ url, client });
      }
    });

    return result;
  }

  /**
   * 获取所有已连接的实例
   */
  static getConnectedInstances(): Array<{
    url: string;
    client: WebSocketClient;
  }> {
    return this.findInstances((client) => client.isConnected());
  }

  /**
   * 获取所有断开连接的实例
   */
  static getDisconnectedInstances(): Array<{
    url: string;
    client: WebSocketClient;
  }> {
    return this.findInstances((client) => !client.isConnected());
  }

  /**
   * 重新连接所有断开的实例
   */
  static reconnectDisconnected(): void {
    const disconnected = this.getDisconnectedInstances();

    disconnected.forEach(({ client }) => {
      client.connect();
    });

    if (disconnected.length > 0) {
      log.debug(
        `[WebSocketManager] 重新连接 ${disconnected.length} 个断开的实例`,
      );
    }
  }

  /**
   * 为所有实例添加事件监听器
   * @param event 事件类型
   * @param listener 监听器函数
   */
  static onAll(
    event: string,
    listener: (data: any, url: string, client: WebSocketClient) => void,
  ): void {
    this.instances.forEach((client, url) => {
      client.on(event as any, (data) => {
        listener(data, url, client);
      });
    });
  }

  /**
   * 获取管理器配置
   */
  static getConfig(): WebSocketManagerConfig {
    return { ...this.config };
  }

  /**
   * 重置管理器(销毁所有实例并重置配置)
   */
  static reset(): void {
    this.destroyAll();
    this.config = {
      singleton: true,
      defaultConfig: {},
    };
  }
}
